import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import NoDataPage from '../../../Components/NoDataPage';
import PageHeading from '../../../Components/PageHeading';
import { useToast } from '../../../Components/CommonToast';
import { Body } from '../../../Constants/Fonts';
import RiskHeatmap from '../components/RiskHeatmap';
import { risk_portal_heatmap } from '../../../services';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// Severity legend tiers → shared tokens.
const LEGEND = [
    { label: 'Low (1–4)', color: 'var(--color-low)' },
    { label: 'Medium (5–9)', color: 'var(--color-medium)' },
    { label: 'High (10–15)', color: 'var(--color-high)' },
    { label: 'Critical (16–25)', color: 'var(--color-critical)' },
];

const ratingColor = (r) => {
    const k = String(r || '').toLowerCase();
    return k === 'critical' ? 'var(--color-critical)'
        : k === 'high' ? 'var(--color-high)'
            : k === 'medium' ? 'var(--color-medium)'
                : k === 'low' ? 'var(--color-low)' : 'var(--color-text-secondary)';
};

// HeatMap — the full 5×5 risk heat map with an inherent / residual toggle and
// per-cell drill-down. Data from risk_portal_heatmap({ type }).
//
// Props: onOpenRisk(id) — open a risk's detail from the drill-down table.
export default function HeatMap({ onOpenRisk }) {
    const [type, setType] = useState('inherent');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCell, setActiveCell] = useState(null); // "L,I"

    const { notify, toastNode } = useToast();

    const load = useCallback(async () => {
        setLoading(true);
        setActiveCell(null);
        try {
            setData(unwrap(await risk_portal_heatmap({ type })));
        } catch (e) {
            notify('Failed to load heat map', 'error');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [type, notify]);

    useEffect(() => { load(); }, [load]);

    const totalAssessed = data?.total_assessed || 0;
    const drillRisks = (activeCell && data?.risks_by_cell?.[activeCell]) || [];
    const noResidual = type === 'residual' && totalAssessed === 0;

    const DRILL_HEADERS = ['Risk ID', 'Description', 'Inherent', 'Residual', 'Rating'];
    const drillRows = drillRisks.map((r) => ({
        data: [
            <Body as="span" variant={2} color="var(--color-primary)" weight="semibold">{r.risk_id || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-primary)">{r.risk || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{r.inherent_score ?? '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{r.residual_score ?? '—'}</Body>,
            <Body as="span" variant={2} color={ratingColor(r.rating)} weight="semibold">{r.rating || '—'}</Body>,
        ],
    }));

    return (
        <div className="rp1_heatmap_page">
            <PageHeading
                title="5×5 Risk Heat Map"
                subtitle={type === 'inherent'
                    ? 'Inherent risk: raw exposure before controls. Score = Likelihood × Impact.'
                    : 'Residual risk: remaining exposure after controls. Only risks with a portal assessment appear here.'}
                actions={(
                    <>
                        <CommonButton text="Inherent Risk" variant={type === 'inherent' ? 'primary' : 'secondary'} onClick={() => setType('inherent')} />
                        <CommonButton text="Residual Risk" variant={type === 'residual' ? 'primary' : 'secondary'} onClick={() => setType('residual')} />
                    </>
                )}
            />

            <div className="rp1_widget" style={{ position: 'relative' }}>
                {loading && <CommonLoader overlay label="Loading heat map…" />}

                <div className="rp1_widget_head">
                    <Body as="span" weight="semibold" color="var(--color-text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Likelihood ↑ &nbsp;/&nbsp; Impact →
                    </Body>
                    <Body as="span" variant={2} color="var(--color-text-muted)">Total Assessed: {totalAssessed}</Body>
                </div>

                <RiskHeatmap
                    matrix={data?.matrix}
                    showScores
                    activeCell={activeCell}
                    onCellClick={(li, im) => {
                        const key = `${li},${im}`;
                        setActiveCell((cur) => (cur === key ? null : key));
                    }}
                />

                {/* Legend */}
                <div className="rp1_heatmap_legend">
                    {LEGEND.map((l) => (
                        <div key={l.label} className="rp1_heatmap_legend_item">
                            <div className="rp1_heatmap_legend_dot" style={{ background: l.color }} />
                            <Body as="span" variant={2} color="var(--color-text-secondary)">{l.label}</Body>
                        </div>
                    ))}
                </div>
            </div>

            {noResidual && (
                <NoDataPage
                    title="No residual risk data"
                    hint='Residual risk needs a formal assessment. Open a risk and click "Assess Risk" to score inherent and residual separately.'
                />
            )}

            {/* Drill-down for the selected cell */}
            {activeCell && drillRows.length > 0 && (
                <div className="rp1_widget">
                    <div className="rp1_widget_head">
                        <Body as="span" weight="semibold" color="var(--color-text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Risks at Likelihood {activeCell.split(',')[0]}, Impact {activeCell.split(',')[1]}
                        </Body>
                    </div>
                    <CommonTable
                        headers={DRILL_HEADERS}
                        rows={drillRows}
                        noPadding
                        noScroll
                        onRowClick={onOpenRisk ? (_row, i) => {
                            const r = drillRisks[i];
                            if (r?.id != null) onOpenRisk(r.id);
                        } : undefined}
                    />
                </div>
            )}

            {toastNode}
        </div>
    );
}
